import { ensureFreshSession } from "@auth/refreshMiddleware";
import { useEffect, useRef, useState } from "react";

import { getConfig } from "../config";
import { clockWord } from "./words";

// Brian's `camera_gateway/live.py`: `<dBB` arrival seconds, keyframe, codec, then the frame.
const HEADER_BYTES = 10;
const CODEC_JPEG = 2;

const hex = (byte: number) => byte.toString(16).padStart(2, "0");

/** `avc1.PPCCLL` from the SPS an Annex-B keyframe carries. */
const avcCodec = (au: Uint8Array) => {
  const sps = au.findIndex(
    (byte, i) =>
      byte === 0 &&
      au[i + 1] === 0 &&
      au[i + 2] === 1 &&
      (au[i + 3] & 0x1f) === 7,
  );
  return `avc1.${[...au.subarray(sps + 4, sps + 7)].map(hex).join("")}`;
};

export const LiveVideo = ({
  roomId,
  roomName,
  aspect,
}: {
  roomId: string;
  roomName: string;
  aspect: number;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [clock, setClock] = useState<string | null>(null);
  const [ended, setEnded] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;
    let socket: WebSocket | null = null;
    let decoder: VideoDecoder | null = null;
    let closed = false;

    const draw = (
      image: VideoFrame | ImageBitmap,
      width: number,
      height: number,
      seconds: number,
    ) => {
      if (canvas.width !== width) canvas.width = width;
      if (canvas.height !== height) canvas.height = height;
      context.drawImage(image, 0, 0);
      image.close();
      setClock(clockWord(new Date(seconds * 1000).toISOString()));
    };

    const onFrame = ({ data }: MessageEvent<ArrayBuffer>) => {
      const header = new DataView(data);
      const arrived = header.getFloat64(0, true);
      const key = header.getUint8(8) === 1;
      const payload = new Uint8Array(data, HEADER_BYTES);
      if (header.getUint8(9) === CODEC_JPEG) {
        void createImageBitmap(
          new Blob([payload], { type: "image/jpeg" }),
        ).then((bitmap) =>
          closed
            ? bitmap.close()
            : draw(bitmap, bitmap.width, bitmap.height, arrived),
        );
        return;
      }
      if (decoder?.state !== "configured") {
        if (!key) return;
        decoder = new VideoDecoder({
          output: (frame) =>
            draw(
              frame,
              frame.displayWidth,
              frame.displayHeight,
              frame.timestamp / 1e6,
            ),
          error: () => (decoder = null),
        });
        decoder.configure({
          codec: avcCodec(payload),
          optimizeForLatency: true,
        });
      }
      decoder.decode(
        new EncodedVideoChunk({
          type: key ? "key" : "delta",
          timestamp: Math.round(arrived * 1e6),
          data: payload,
        }),
      );
    };

    void ensureFreshSession().then((session) => {
      if (closed || !session) return;
      socket = new WebSocket(
        `${getConfig().wsUrl}/perception/rooms/${roomId}/live`,
        ["scribenote.auth.bearer.v1", session.accessToken],
      );
      socket.binaryType = "arraybuffer";
      socket.onmessage = onFrame;
      socket.onclose = () => setEnded(true);
    });

    return () => {
      closed = true;
      socket?.close();
      if (decoder?.state === "configured") decoder.close();
    };
  }, [roomId]);

  return (
    <div
      data-dd-privacy="hidden"
      className="relative w-full overflow-hidden bg-recess"
      style={{ aspectRatio: aspect, width: `min(100cqw, 100cqh * ${aspect})` }}
    >
      <canvas
        ref={canvasRef}
        aria-label={`Live video from ${roomName}`}
        className="absolute inset-0 h-full w-full object-contain"
      />
      {clock && (
        <span className="absolute top-1 left-1 rounded-paper bg-card px-1 font-mono text-fine-print text-ink-body">
          {clock}
        </span>
      )}
      {(ended || !clock) && (
        <p className="absolute inset-0 flex items-center justify-center p-4 text-center text-sm text-annotation">
          {ended ? "The live video stopped." : "Waiting for the camera."}
        </p>
      )}
    </div>
  );
};
